import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { ITruckDeviceStatus, defaultValue } from 'app/shared/model/truck-device-status.model';
import { ITruckImplement } from 'app/shared/model/truck-implement.model';
import { TruckDeviceLockStatus } from 'app/shared/model/enumerations/truck-device-lock-status.model';

const initialState: EntityState<ITruckDeviceStatus> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/truck-device-statuses';
const apiUrlEntity = 'api/truck-device-status';

// Actions

export const getEntities = createAsyncThunk('truckDeviceStatus/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<ITruckDeviceStatus[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'truckDeviceStatus/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ITruckDeviceStatus>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'truckDeviceStatus/create_entity',
  async (entity: ITruckDeviceStatus, thunkAPI) => {
    const result = await axios.post<ITruckDeviceStatus>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'truckDeviceStatus/update_entity',
  async (entity: ITruckDeviceStatus, thunkAPI) => {
    const result = await axios.put<ITruckDeviceStatus>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'truckDeviceStatus/partial_update_entity',
  async (entity: ITruckDeviceStatus, thunkAPI) => {
    const result = await axios.patch<ITruckDeviceStatus>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'truckDeviceStatus/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ITruckDeviceStatus>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

interface ICreateEntityByTruck {
  truckImplement: ITruckImplement;
  status: TruckDeviceLockStatus;
}

export const createEntityByTruck = createAsyncThunk(
  'truckDeviceStatus/create_entity_by_truck',
  async ({ truckImplement, status }: ICreateEntityByTruck) => {
    const result = await axios.post<ICreateEntityByTruck>(`${apiUrlEntity}/truck`, { truckImplement, status });
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getEntityByTruck = createAsyncThunk(
  'truckDeviceStatus/fetch_entity_by_truck',
  async (truckId: string | number) => {
    const requestUrl = `${apiUrlEntity}/truck/${truckId}`;
    return axios.get<ITruckDeviceStatus>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

// slice

export const TruckDeviceStatusSlice = createEntitySlice({
  name: 'truckDeviceStatus',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(getEntityByTruck.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, createEntityByTruck, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getEntityByTruck), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, createEntityByTruck, updateEntity, partialUpdateEntity, deleteEntity, getEntityByTruck), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = TruckDeviceStatusSlice.actions;

// Reducer
export default TruckDeviceStatusSlice.reducer;
